import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AgentConfig } from "../../shared/types/agent";
import { agentApi } from "../services/agentApi";
import { AgentList } from "../components/AgentSettings/AgentList";

export const AgentListView: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentConfig[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agentApi.list();
      if (response.success && response.data) {
        setAgents(response.data.agents || []);
      } else {
        setError(response.error || 'Failed to load agents');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = () => {
    navigate('/app/settings/agents/new');
  };

  const handleEditAgent = (agent: AgentConfig) => {
    navigate(`/app/settings/agents/edit/${agent.id}`);
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      const response = await agentApi.delete(agentId);
      if (response.success) {
        setAgents(prev => prev.filter(a => a.id !== agentId));
        if (selectedAgent?.id === agentId) {
          setSelectedAgent(null);
        }
      } else {
        setError(response.error || 'Failed to delete agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const handleUpdateAgent = async (agentId: string, updates: Partial<AgentConfig>) => {
    try {
      // Convert AgentConfig to AgentUpdateRequest
      const updateRequest: any = { ...updates };
      if (updates.tools) {
        updateRequest.tools = updates.tools.map(t => typeof t === 'string' ? t : t.id);
      }
      
      const response = await agentApi.update(agentId, updateRequest);
      if (response.success && response.data) {
        setAgents(prev => prev.map(a => a.id === agentId ? { ...a, ...response.data } : a));
        if (selectedAgent?.id === agentId) {
          setSelectedAgent({ ...selectedAgent, ...response.data });
        }
      } else {
        setError(response.error || 'Failed to update agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent');
    }
  };

  return (
    <div className="h-full overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Agent Management</h2>
              <p className="text-gray-400 mt-1">Manage your AI agent configurations</p>
            </div>
            <button
              onClick={handleCreateAgent}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Agent
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <AgentList
            agents={agents}
            selectedAgent={selectedAgent}
            onSelectAgent={setSelectedAgent}
            onEditAgent={handleEditAgent}
            onDeleteAgent={handleDeleteAgent}
            onUpdateAgent={handleUpdateAgent}
            loading={loading}
            error={error}
            className="h-full"
          />
        </div>
      </div>
    </div>
  );
};
